import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Manages image loading state during FileDetailModal navigation.
 * Keeps displayedImageUrl stable until the viewer commits a successful decode.
 */
export const useImageLoading = (
  currentFileId: string | number | undefined,
  currentImageUrl: string | undefined,
) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [displayedImageUrl, setDisplayedImageUrl] = useState<string | undefined>(undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isFirstLoad = useRef(true);
  const currentImageUrlRef = useRef(currentImageUrl);
  currentImageUrlRef.current = currentImageUrl;

  const handleLoadStart = useCallback(() => {
    setImageLoading(true);
    setShowSpinner(false);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShowSpinner(true);
    }, 300);
  }, []);

  const handleLoadComplete = useCallback((loadedUrl?: string) => {
    const url = loadedUrl ?? currentImageUrlRef.current;
    // Rapid navigation: ignore a decode that is no longer the current target.
    if (!url || url !== currentImageUrlRef.current) {
      return;
    }

    setImageLoading(false);
    setShowSpinner(false);
    setDisplayedImageUrl(url);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!currentImageUrl) {
      setImageLoading(false);
      setShowSpinner(false);
      setDisplayedImageUrl(undefined);
      isFirstLoad.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return;
    }

    if (isFirstLoad.current || !displayedImageUrl) {
      setDisplayedImageUrl(currentImageUrl);
      setImageLoading(false);
      setShowSpinner(false);
      isFirstLoad.current = false;
      return;
    }

    if (currentImageUrl !== displayedImageUrl) {
      handleLoadStart();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentFileId, currentImageUrl, displayedImageUrl, handleLoadStart]);

  return {
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,
  };
};
